import React, { useState, useEffect } from 'react';
import { getPartyListAPI, Party, getLedgerListAPI, LedgerResponse } from '../../../services/adminApi';
import styles from './Ledger.module.css';

interface LedgerEntryDisplay {
  id: string;
  date: string;
  type: 'Buy' | 'Sell' | 'Swap';
  partyId: string;
  partyName: string;
  description: string;
  in: number;
  out: number;
  balance: number;
}

const Ledger: React.FC = () => {
  const [parties, setParties] = useState<Party[]>([]);
  const [selectedParty, setSelectedParty] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionType, setTransactionType] = useState('all');
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntryDisplay[]>([]);
  const [loading, setLoading] = useState(false);

  // Load parties on component mount
  useEffect(() => {
    const loadParties = async () => {
      try {
        const response = await getPartyListAPI({ pageNo: 1, pageSize: 100 });
        setParties((response.data || []).filter(p => p.isActive));
      } catch (error) {
        console.error('Failed to load parties:', error);
      }
    };
    loadParties();
  }, []);

  // Load ledger entries based on filters
  useEffect(() => {
    loadLedgerEntries();
  }, [selectedParty, startDate, endDate, transactionType]);

  const loadLedgerEntries = async () => {
    try {
      setLoading(true);

      // Build request object following the same pattern as Reports component
      const request: any = {
        pageNo: 1,
        pageSize: 100,
        searchText:'',
        isActive:null,
        partyId:0,
        transactionTypeId:0,
        fromDate: null,
        toDate: null
      };

      // Add optional filters
      if (selectedParty !== 'all') {
        request.partyId = parseInt(selectedParty);
      }
      if (startDate) {
        request.fromDate = startDate;
      }
      if (endDate) {
        request.toDate = endDate;
      }
      if (transactionType !== 'all') {
        request.transactionTypeId = parseInt(transactionType);
      }

      const response = await getLedgerListAPI(request);

      if (response.isSuccess && response.data && Array.isArray(response.data)) {
        const entries: LedgerEntryDisplay[] = response.data.map((entry: LedgerResponse) => {
          const typeStr = entry.transactionType || '';
          const capitalizedType = typeStr.charAt(0).toUpperCase() + typeStr.slice(1).toLowerCase();
          
          // Helper function to parse numeric values, handling "₹-" and other edge cases
          const parseAmount = (value: any): number => {
            if (value === null || value === undefined || value === '' || value === '₹-' || value === '-') {
              return 0;
            }
            if (typeof value === 'string') {
              // Remove currency symbol and convert to number
              const cleaned = value.replace(/[₹$,]/g, '').trim();
              const parsed = parseFloat(cleaned);
              return isNaN(parsed) ? 0 : parsed;
            }
            const num = Number(value);
            return isNaN(num) ? 0 : num;
          };
          
          return {
            id: entry.id?.toString() || '',
            date: entry.transactionDate || '',
            type: (capitalizedType === 'Buy' || capitalizedType === 'Sell' || capitalizedType === 'Swap') 
              ? capitalizedType as 'Buy' | 'Sell' | 'Swap'
              : 'Buy' as 'Buy' | 'Sell' | 'Swap',
            partyId: entry.partyId?.toString() || '',
            partyName: entry.partyName || '',
            description: entry.remarks || '',
            in: parseAmount(entry.in),
            out: parseAmount(entry.out),
            balance: parseAmount(entry.balance),
          };
        });
        setLedgerEntries(entries);
      } else {
        setLedgerEntries([]);
      }
    } catch (err) {
      console.error('Ledger fetch error:', err);
      setLedgerEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePartyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedParty(e.target.value);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const handleTransactionTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTransactionType(e.target.value);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Ledger</h1>
          <p className={styles.subtitle}>Transaction history with running balance</p>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filtersContainer}>
        <div className={styles.filterGroup}>
          <label htmlFor="party" className={styles.filterLabel}>
            Party
          </label>
          <select
            id="party"
            value={selectedParty}
            onChange={handlePartyChange}
            className={styles.filterSelect}
          >
            <option value="all">All Parties</option>
            {parties.map((party) => (
              <option key={party.id} value={party.id?.toString()}>
                {party.partyName}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="startDate" className={styles.filterLabel}>
            Start Date
          </label>
          <input
            id="startDate"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className={styles.filterInput}
            placeholder="dd---yyyy"
          />
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="endDate" className={styles.filterLabel}>
            End Date
          </label>
          <input
            id="endDate"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            className={styles.filterInput}
            placeholder="dd---yyyy"
          />
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="transactionType" className={styles.filterLabel}>
            Transaction Type
          </label>
          <select
            id="transactionType"
            value={transactionType}
            onChange={handleTransactionTypeChange}
            className={styles.filterSelect}
          >
            <option value="all">All Types</option>
            <option value="buy">Buy</option>
            <option value="sell">Sell</option>
            <option value="swap">Swap</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading ledger...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>DATE</th>
                <th>TYPE</th>
                <th>PARTY</th>
                <th>DESCRIPTION</th>
                <th>IN</th>
                <th>OUT</th>
                <th>BALANCE</th>
              </tr>
            </thead>
            <tbody>
              {ledgerEntries.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.emptyState}>No ledger entries found</td>
                </tr>
              ) : (
                ledgerEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{new Date(entry.date).toLocaleDateString('en-IN')}</td>
                    <td>
                      <span className={`${styles.typeBadge} ${styles[entry.type.toLowerCase()]}`}>
                        {entry.type}
                      </span>
                    </td>
                    <td>{entry.partyName}</td>
                    <td>{entry.description}</td>
                    <td className={styles.in}>
                      {entry.in === 0 ? '0' : `₹${entry.in.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </td>
                    <td className={styles.out}>
                      {entry.out === 0 ? '0' : `₹${entry.out.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </td>
                    <td className={styles.balance}>
                      {entry.balance === 0 ? '0' : `₹${entry.balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Ledger;
